import { useEffect, useMemo, useState } from "react";
import { Alert, Avatar, Button, Card, Input, Modal, Progress, Radio, Select, Space, Switch, Table, Tabs, Tag, Typography } from "antd";
import {
  Activity, AlertCircle, AlertTriangle, Bell, Building2, CalendarCheck, CheckCircle,
  Clock, Edit3, Loader2, Mail, MapPin, Phone, Plus, RefreshCw, Scissors, Settings2,
  Star, TrendingUp, UserCheck, Users, Video, VideoOff, Wifi, WifiOff, DollarSign,
} from "lucide-react";

const { Text, Title } = Typography;

type LocationStatus = "open" | "closed" | "busy";
type AlertSeverity = "info" | "warning" | "error";

interface LocationAlert { id: string; type: AlertSeverity; message: string; time: string; }
interface LocationData {
  id: string; name: string; address: string; city: string; state: string; zip: string;
  phone: string; email: string; status: LocationStatus; isActive: boolean; manager: string;
  capacity: number; hoursOpen: string; hoursClose: string; staffOnDuty: string[];
  stats: {
    revenueToday: number; revenueYesterday: number; revenueWeek: number; revenueMonth: number;
    appointmentsToday: number; appointmentsCompleted: number; walkInsToday: number;
    queueLength: number; occupancyPercent: number; clientsServedToday: number;
    avgWaitMinutes: number; topService: string; rating: number; newClientsMonth: number;
  };
  alerts: LocationAlert[];
  hex: string;
}
interface RtspConfig { url: string; username: string; password: string; resolution: "auto" | "720p" | "1080p" | "4k"; enabled: boolean; }
interface StaffPerf { name: string; location: string; hex: string; clientsThisMonth: number; clientsLastMonth: number; retentionRate: number; revenueMonth: number; rating: number; topService: string; isManager?: boolean; }

const INITIAL_LOCATIONS: LocationData[] = [
  { id: "loc1", name: "Downtown", address: "142 Main Street", city: "New York", state: "NY", zip: "10001", phone: "(212) 555-0142", email: "downtown@hairplane.com", status: "open", isActive: true, manager: "Jordan Blake", capacity: 8, hoursOpen: "9:00 AM", hoursClose: "8:00 PM", staffOnDuty: ["Maya Chen", "Jordan Blake", "Sam Williams", "Chris Rivera"], stats: { revenueToday: 1840, revenueYesterday: 1620, revenueWeek: 11200, revenueMonth: 43800, appointmentsToday: 14, appointmentsCompleted: 9, walkInsToday: 5, queueLength: 2, occupancyPercent: 75, clientsServedToday: 9, avgWaitMinutes: 12, topService: "Haircut", rating: 4.8, newClientsMonth: 18 }, alerts: [{ id: "a1", type: "info", message: "2 walk-ins in queue — ~12 min wait", time: "5m ago" }], hex: "#6366f1" },
  { id: "loc2", name: "Midtown", address: "567 Park Avenue", city: "New York", state: "NY", zip: "10022", phone: "(212) 555-0567", email: "midtown@hairplane.com", status: "busy", isActive: true, manager: "Taylor Morgan", capacity: 6, hoursOpen: "9:00 AM", hoursClose: "7:00 PM", staffOnDuty: ["Taylor Morgan", "Alex Kim", "Priya Patel"], stats: { revenueToday: 1120, revenueYesterday: 980, revenueWeek: 7800, revenueMonth: 31200, appointmentsToday: 11, appointmentsCompleted: 8, walkInsToday: 3, queueLength: 1, occupancyPercent: 100, clientsServedToday: 8, avgWaitMinutes: 20, topService: "Color Treatment", rating: 4.6, newClientsMonth: 11 }, alerts: [{ id: "a2", type: "warning", message: "All 6 chairs occupied — high demand", time: "2m ago" }, { id: "a3", type: "info", message: "Taylor on break — returns 3:15 PM", time: "1m ago" }], hex: "#10b981" },
  { id: "loc3", name: "Westside", address: "88 West 34th Street", city: "New York", state: "NY", zip: "10001", phone: "(212) 555-0088", email: "westside@hairplane.com", status: "open", isActive: true, manager: "Chris Rivera", capacity: 5, hoursOpen: "10:00 AM", hoursClose: "7:00 PM", staffOnDuty: ["Chris Rivera", "Sam Williams"], stats: { revenueToday: 680, revenueYesterday: 820, revenueWeek: 4200, revenueMonth: 17800, appointmentsToday: 8, appointmentsCompleted: 6, walkInsToday: 2, queueLength: 0, occupancyPercent: 40, clientsServedToday: 6, avgWaitMinutes: 5, topService: "Beard Trim", rating: 4.5, newClientsMonth: 7 }, alerts: [{ id: "a4", type: "warning", message: "1 staff member called out sick today", time: "1h ago" }], hex: "#f59e0b" },
];

const STAFF_PERFORMANCE: StaffPerf[] = [
  { name: "Maya Chen", location: "Downtown", hex: "#6366f1", clientsThisMonth: 92, clientsLastMonth: 78, retentionRate: 0.88, revenueMonth: 7400, rating: 4.9, topService: "Balayage" },
  { name: "Jordan Blake", location: "Downtown", hex: "#6366f1", clientsThisMonth: 84, clientsLastMonth: 80, retentionRate: 0.85, revenueMonth: 6900, rating: 4.7, topService: "Haircut", isManager: true },
  { name: "Taylor Morgan", location: "Midtown", hex: "#10b981", clientsThisMonth: 78, clientsLastMonth: 65, retentionRate: 0.82, revenueMonth: 6100, rating: 4.8, topService: "Color Treatment", isManager: true },
  { name: "Alex Kim", location: "Midtown", hex: "#10b981", clientsThisMonth: 71, clientsLastMonth: 70, retentionRate: 0.79, revenueMonth: 5500, rating: 4.6, topService: "Cut & Style" },
  { name: "Priya Patel", location: "Midtown", hex: "#10b981", clientsThisMonth: 63, clientsLastMonth: 58, retentionRate: 0.76, revenueMonth: 4900, rating: 4.5, topService: "Highlights" },
  { name: "Sam Williams", location: "Westside", hex: "#f59e0b", clientsThisMonth: 55, clientsLastMonth: 49, retentionRate: 0.82, revenueMonth: 4200, rating: 4.7, topService: "Beard Trim" },
  { name: "Chris Rivera", location: "Westside", hex: "#f59e0b", clientsThisMonth: 48, clientsLastMonth: 42, retentionRate: 0.80, revenueMonth: 3800, rating: 4.5, topService: "Haircut", isManager: true },
];

const DEFAULT_RTSP: Record<string, RtspConfig> = {
  loc1: { url: "rtsp://192.168.1.101:554/live/main", username: "admin", password: "", resolution: "auto", enabled: true },
  loc2: { url: "rtsp://192.168.1.102:554/live/main", username: "admin", password: "", resolution: "1080p", enabled: true },
  loc3: { url: "rtsp://192.168.1.103:554/live/main", username: "admin", password: "", resolution: "auto", enabled: true },
};

function fmt$(n: number) { return `$${n.toLocaleString()}`; }
function revDelta(today: number, yesterday: number) {
  if (yesterday === 0) return { pct: 0, up: true };
  const pct = Math.round(((today - yesterday) / yesterday) * 100);
  return { pct: Math.abs(pct), up: pct >= 0 };
}
function raiseSignal(sp: StaffPerf) {
  const growth = sp.clientsLastMonth > 0 ? ((sp.clientsThisMonth - sp.clientsLastMonth) / sp.clientsLastMonth) * 100 : 0;
  if (growth >= 15 && sp.retentionRate >= 0.85) return { label: "Strong raise candidate", color: "success", growth };
  if (growth >= 10 && sp.retentionRate >= 0.80) return { label: "Consider a raise", color: "processing", growth };
  if (growth >= 5) return { label: "Merit increase possible", color: "purple", growth };
  if (growth >= 0) return { label: "Stable — monitor retention", color: "default", growth };
  return { label: "Declining — needs review", color: "error", growth };
}

function LiveTimestamp() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 1000); return () => clearInterval(id); }, []);
  return <span>{t.toLocaleTimeString("en-US", { hour12: false })}</span>;
}

function RtspSettingsModal({ open, config, locName, onSave, onClose }: { open: boolean; config: RtspConfig; locName: string; onSave: (c: RtspConfig) => void; onClose: () => void }) {
  const [draft, setDraft] = useState<RtspConfig>(config);
  const [testing, setTesting] = useState(false);
  const [testOk, setTestOk] = useState<boolean | null>(null);

  useEffect(() => { if (open) { setDraft(config); setTestOk(null); } }, [open, config]);

  function test() {
    if (!draft.url) return;
    setTesting(true); setTestOk(null);
    setTimeout(() => { setTesting(false); setTestOk(true); }, 1800);
  }

  return (
    <Modal title={<span><Video size={15} style={{ marginRight: 6 }} />RTSP Camera Settings — {locName}</span>} open={open} onCancel={onClose} onOk={() => onSave(draft)} okText="Save Settings">
      <Space direction="vertical" size={14} style={{ width: "100%", marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div><Text strong style={{ fontSize: 13 }}>Enable camera feed</Text><div style={{ fontSize: 11, color: "#9ca3af" }}>Show live video in dashboards</div></div>
          <Switch checked={draft.enabled} onChange={(v) => setDraft({ ...draft, enabled: v })} />
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>RTSP Stream URL</Text>
          <Input value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} placeholder="rtsp://192.168.1.100:554/live/main" style={{ marginTop: 4, fontFamily: "monospace" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><Text type="secondary" style={{ fontSize: 11 }}>Username</Text><Input value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })} style={{ marginTop: 4 }} /></div>
          <div><Text type="secondary" style={{ fontSize: 11 }}>Password</Text><Input.Password value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} style={{ marginTop: 4 }} /></div>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 6 }}>Stream Resolution</Text>
          <Radio.Group value={draft.resolution} onChange={(e) => setDraft({ ...draft, resolution: e.target.value })} style={{ width: "100%" }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Radio value="auto">Auto — let the camera decide</Radio>
              <Radio value="720p">720p HD — low bandwidth</Radio>
              <Radio value="1080p">1080p Full HD <Tag color="success" style={{ marginLeft: 6 }}>Recommended</Tag></Radio>
              <Radio value="4k">4K Ultra HD <Tag color="warning" style={{ marginLeft: 6 }}>High bandwidth</Tag></Radio>
            </Space>
          </Radio.Group>
        </div>
        <Button block icon={testing ? <Loader2 size={14} className="spin" /> : <Wifi size={14} />} disabled={!draft.url || testing} onClick={test}>
          {testing ? "Testing connection…" : "Test Connection"}
        </Button>
        {testOk && <Text type="success" style={{ fontSize: 12 }}><CheckCircle size={12} style={{ marginRight: 4 }} />Stream is accessible · Connection successful</Text>}
        <Alert type="warning" showIcon message="Browsers cannot play RTSP directly. Connect your cameras to a media server (MediaMTX, Wowza, Ant Media) and enter the transcoded stream URL above." />
      </Space>
    </Modal>
  );
}

function CameraFeedWidget({ locId, locName, config, onConfigChange }: { locId: string; locName: string; config: RtspConfig; onConfigChange: (c: RtspConfig) => void }) {
  const [showSettings, setShowSettings] = useState(false);
  const [state, setState] = useState<"idle" | "connecting" | "live">("idle");

  useEffect(() => {
    if (config.enabled && config.url) {
      setState("connecting");
      const t = setTimeout(() => setState("live"), 1200);
      return () => clearTimeout(t);
    }
    setState("idle");
  }, [config.enabled, config.url]);

  return (
    <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", background: "#111827", aspectRatio: "16/9" }}>
      {state === "live" && (
        <>
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${DEFAULT_RTSP[locId]?.url ? "#1f2937" : "#111827"}, #030712)` }} />
          <div style={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 6 }}>
            <span style={{ background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 6, display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444" }} />LIVE
            </span>
            <span style={{ background: "rgba(0,0,0,0.5)", color: "rgba(255,255,255,0.8)", fontSize: 10, fontFamily: "monospace", padding: "3px 8px", borderRadius: 6 }}><LiveTimestamp /></span>
          </div>
          <div style={{ position: "absolute", bottom: 8, left: 8, background: "rgba(0,0,0,0.5)", color: "#fff", fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 6 }}>{locName} · CAM 1</div>
        </>
      )}
      {state === "connecting" && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, color: "#9ca3af" }}>
          <Loader2 size={20} className="spin" /><span style={{ fontSize: 12 }}>Connecting to stream…</span>
        </div>
      )}
      {state === "idle" && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, color: "#6b7280" }}>
          <VideoOff size={20} /><span style={{ fontSize: 12 }}>No stream configured</span>
        </div>
      )}
      <button onClick={() => setShowSettings(true)} style={{ position: "absolute", top: 8, right: 8, width: 26, height: 26, borderRadius: 6, background: "rgba(0,0,0,0.6)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <Settings2 size={13} color="#fff" />
      </button>
      <RtspSettingsModal open={showSettings} config={config} locName={locName} onSave={(c) => { onConfigChange(c); setShowSettings(false); }} onClose={() => setShowSettings(false)} />
    </div>
  );
}

function LocationLiveCard({ loc, cameraConfig, onCameraConfigChange }: { loc: LocationData; cameraConfig: RtspConfig; onCameraConfigChange: (c: RtspConfig) => void }) {
  const delta = revDelta(loc.stats.revenueToday, loc.stats.revenueYesterday);
  const apptPct = loc.stats.appointmentsToday > 0 ? Math.round((loc.stats.appointmentsCompleted / loc.stats.appointmentsToday) * 100) : 0;
  const occupiedChairs = Math.round((loc.capacity * loc.stats.occupancyPercent) / 100);
  const statusColor = loc.status === "open" ? "success" : loc.status === "busy" ? "warning" : "default";

  return (
    <Card size="small" style={{ borderTop: `3px solid ${loc.hex}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <div><Text strong style={{ fontSize: 15 }}>{loc.name}</Text><div style={{ fontSize: 11, color: "#9ca3af" }}><MapPin size={10} style={{ marginRight: 3 }} />{loc.address}, {loc.city}</div></div>
        <Tag color={statusColor}>{loc.status === "busy" ? "At Capacity" : loc.status === "open" ? "Open" : "Closed"}</Tag>
      </div>

      <CameraFeedWidget locId={loc.id} locName={loc.name} config={cameraConfig} onConfigChange={onCameraConfigChange} />

      <div style={{ background: "#fafafa", borderRadius: 10, padding: 10, margin: "10px 0" }}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt$(loc.stats.revenueToday)}</div>
        <Text type="secondary" style={{ fontSize: 11 }}>Today's revenue · </Text>
        <Text style={{ fontSize: 11, color: delta.up ? "#059669" : "#ef4444", fontWeight: 600 }}>{delta.up ? "▲" : "▼"} {delta.pct}% vs yesterday</Text>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}><Text type="secondary">Appointments</Text><Text strong style={{ fontSize: 11 }}>{loc.stats.appointmentsCompleted} done · {loc.stats.appointmentsToday - loc.stats.appointmentsCompleted} left</Text></div>
        <Progress percent={apptPct} showInfo={false} strokeColor={loc.hex} size="small" />
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#8c8c8c", marginBottom: 6 }}><span>Chair Occupancy</span><Text strong style={{ fontSize: 11 }}>{occupiedChairs}/{loc.capacity} chairs</Text></div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {Array.from({ length: loc.capacity }).map((_, i) => (
            <div key={i} style={{ width: 22, height: 22, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", background: i < occupiedChairs ? `${loc.hex}20` : "#f9fafb", border: `1.5px solid ${i < occupiedChairs ? loc.hex : "#e5e7eb"}` }}>
              <Scissors size={10} color={i < occupiedChairs ? loc.hex : "#d1d5db"} />
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <Avatar.Group max={{ count: 5 }} size={26}>
          {loc.staffOnDuty.map((n) => <Avatar key={n} style={{ background: loc.hex, fontSize: 11 }}>{n.charAt(0)}</Avatar>)}
        </Avatar.Group>
        <Text type="secondary" style={{ fontSize: 11 }}>{loc.staffOnDuty.length} staff on duty</Text>
      </div>

      <Space size={6} style={{ fontSize: 11, color: "#8c8c8c", flexWrap: "wrap" }}>
        <span><UserCheck size={11} style={{ marginRight: 3 }} />{loc.stats.clientsServedToday} served</span>
        <span>·</span>
        <span><Users size={11} style={{ marginRight: 3 }} />{loc.stats.walkInsToday} walk-ins</span>
        {loc.stats.queueLength > 0 && <Tag color="warning" style={{ fontSize: 10 }}><Clock size={9} style={{ marginRight: 3 }} />{loc.stats.queueLength} in queue · ~{loc.stats.avgWaitMinutes}m</Tag>}
      </Space>
    </Card>
  );
}

function AlertsPanel({ locations }: { locations: LocationData[] }) {
  const all = locations.flatMap((loc) => loc.alerts.map((a) => ({ ...a, locName: loc.name, locHex: loc.hex })));
  const iconFor = (t: AlertSeverity) => t === "warning" ? <AlertTriangle size={13} color="#f59e0b" /> : t === "error" ? <AlertCircle size={13} color="#ef4444" /> : <AlertCircle size={13} color="#3b82f6" />;

  return (
    <Card size="small" title={<span><Bell size={13} style={{ marginRight: 6 }} />Alerts {all.length > 0 && <Tag color="error" style={{ marginLeft: 6 }}>{all.length}</Tag>}</span>}>
      {all.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20, color: "#9ca3af" }}><CheckCircle size={22} color="#34d399" /><div style={{ fontSize: 12, marginTop: 6 }}>All clear across all locations</div></div>
      ) : (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {all.map((a) => (
            <div key={a.id} style={{ display: "flex", gap: 8, padding: 8, borderRadius: 10, background: a.type === "warning" ? "#fffbeb" : a.type === "error" ? "#fef2f2" : "#eff6ff" }}>
              {iconFor(a.type)}
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}><Tag color={a.locHex} style={{ fontSize: 9, background: a.locHex, color: "#fff", border: "none" }}>{a.locName}</Tag><Text type="secondary" style={{ fontSize: 10 }}>{a.time}</Text></div>
                <Text style={{ fontSize: 12 }}>{a.message}</Text>
              </div>
            </div>
          ))}
        </Space>
      )}
    </Card>
  );
}

function DashboardTab({ locations, setLocations, rtspConfigs, onRtspChange }: { locations: LocationData[]; setLocations: React.Dispatch<React.SetStateAction<LocationData[]>>; rtspConfigs: Record<string, RtspConfig>; onRtspChange: (id: string, c: RtspConfig) => void }) {
  const [secondsAgo, setSecondsAgo] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setLocations((prev) => prev.map((loc) => {
        if (loc.status === "closed") return loc;
        const bump = Math.floor(Math.random() * 60);
        const completeOne = Math.random() > 0.6 && loc.stats.appointmentsCompleted < loc.stats.appointmentsToday;
        return { ...loc, stats: { ...loc.stats, revenueToday: loc.stats.revenueToday + bump, appointmentsCompleted: completeOne ? loc.stats.appointmentsCompleted + 1 : loc.stats.appointmentsCompleted, clientsServedToday: completeOne ? loc.stats.clientsServedToday + 1 : loc.stats.clientsServedToday } };
      }));
      setSecondsAgo(0);
    }, 25_000);
    return () => clearInterval(id);
  }, [setLocations]);

  useEffect(() => { const id = setInterval(() => setSecondsAgo((s) => s + 1), 1000); return () => clearInterval(id); }, []);

  const totals = useMemo(() => ({
    revenue: locations.reduce((s, l) => s + l.stats.revenueToday, 0),
    appts: locations.reduce((s, l) => s + l.stats.appointmentsToday, 0),
    completed: locations.reduce((s, l) => s + l.stats.appointmentsCompleted, 0),
    served: locations.reduce((s, l) => s + l.stats.clientsServedToday, 0),
    walkins: locations.reduce((s, l) => s + l.stats.walkInsToday, 0),
    staff: locations.reduce((s, l) => s + l.staffOnDuty.length, 0),
    queue: locations.reduce((s, l) => s + l.stats.queueLength, 0),
  }), [locations]);

  const topLocation = [...locations].sort((a, b) => b.stats.revenueToday - a.stats.revenueToday)[0];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div><Title level={5} style={{ margin: 0 }}>Live Overview</Title><Text type="secondary" style={{ fontSize: 12 }}>{locations.filter((l) => l.status !== "closed").length} of {locations.length} locations open</Text></div>
        <Space>
          <Text type="secondary" style={{ fontSize: 11 }}><RefreshCw size={11} style={{ marginRight: 4 }} />Updated {secondsAgo < 5 ? "just now" : `${secondsAgo}s ago`}</Text>
          <Tag color="success">● LIVE</Tag>
        </Space>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        <Card size="small"><DollarSign size={16} color="#4f46e5" /><div style={{ fontSize: 20, fontWeight: 700 }}>{fmt$(totals.revenue)}</div><Text type="secondary" style={{ fontSize: 11 }}>Today's Revenue</Text></Card>
        <Card size="small"><CalendarCheck size={16} color="#2563eb" /><div style={{ fontSize: 20, fontWeight: 700 }}>{totals.completed}/{totals.appts}</div><Text type="secondary" style={{ fontSize: 11 }}>{totals.appts - totals.completed} remaining</Text></Card>
        <Card size="small"><UserCheck size={16} color="#059669" /><div style={{ fontSize: 20, fontWeight: 700 }}>{totals.served}</div><Text type="secondary" style={{ fontSize: 11 }}>+{totals.walkins} walk-ins</Text></Card>
        <Card size="small"><Users size={16} color="#7c3aed" /><div style={{ fontSize: 20, fontWeight: 700 }}>{totals.staff}</div><Text type="secondary" style={{ fontSize: 11 }}>{totals.queue > 0 ? `${totals.queue} in queue` : "No queue"}</Text></Card>
      </div>

      {topLocation && (
        <Alert
          style={{ marginBottom: 16, borderColor: `${topLocation.hex}40`, background: `${topLocation.hex}08` }}
          type="info"
          icon={<Star size={14} color={topLocation.hex} />}
          showIcon
          message={<span><b style={{ color: topLocation.hex }}>{topLocation.name}</b> is today's top performer — <b>{fmt$(topLocation.stats.revenueToday)}</b> · {topLocation.stats.rating}⭐ · Top service: {topLocation.stats.topService}</span>}
        />
      )}

      <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {locations.map((loc) => (
            <LocationLiveCard key={loc.id} loc={loc} cameraConfig={rtspConfigs[loc.id] ?? { url: "", username: "", password: "", resolution: "auto", enabled: false }} onCameraConfigChange={(c) => onRtspChange(loc.id, c)} />
          ))}
        </div>
        <AlertsPanel locations={locations} />
      </div>
    </div>
  );
}

function LocationEditModal({ open, initial, onSave, onClose }: { open: boolean; initial: LocationData | null; onSave: (d: Partial<LocationData> & { name: string }) => void; onClose: () => void }) {
  const [draft, setDraft] = useState({ name: "", address: "", city: "", state: "", zip: "", phone: "", email: "", manager: "", capacity: "6", hoursOpen: "9:00 AM", hoursClose: "7:00 PM" });

  useEffect(() => {
    if (open) {
      setDraft(initial ? { name: initial.name, address: initial.address, city: initial.city, state: initial.state, zip: initial.zip, phone: initial.phone, email: initial.email, manager: initial.manager, capacity: String(initial.capacity), hoursOpen: initial.hoursOpen, hoursClose: initial.hoursClose } : { name: "", address: "", city: "", state: "", zip: "", phone: "", email: "", manager: "", capacity: "6", hoursOpen: "9:00 AM", hoursClose: "7:00 PM" });
    }
  }, [open, initial]);

  const field = (label: string, key: keyof typeof draft, ph = "") => (
    <div><Text type="secondary" style={{ fontSize: 11 }}>{label}</Text><Input value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} placeholder={ph} style={{ marginTop: 4 }} /></div>
  );

  return (
    <Modal title={initial ? "Edit Location" : "Add New Location"} open={open} onCancel={onClose} onOk={() => { onSave({ ...draft, capacity: parseInt(draft.capacity) || 6 } as any); onClose(); }} okText={initial ? "Save Changes" : "Add Location"} okButtonProps={{ disabled: !draft.name.trim() }}>
      <Space direction="vertical" size={12} style={{ width: "100%", marginTop: 12 }}>
        {field("Location Name", "name", "e.g. Downtown")}
        {field("Street Address", "address", "123 Main Street")}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10 }}>{field("City", "city", "New York")}{field("State", "state", "NY")}{field("ZIP", "zip", "10001")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>{field("Phone", "phone", "(555) 000-0000")}{field("Email", "email", "location@salon.com")}</div>
        {field("Manager", "manager", "Staff member name")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>{field("Chairs", "capacity", "6")}{field("Opens", "hoursOpen", "9:00 AM")}{field("Closes", "hoursClose", "7:00 PM")}</div>
      </Space>
    </Modal>
  );
}

function LocationsTab({ locations, setLocations, rtspConfigs, onRtspChange }: { locations: LocationData[]; setLocations: React.Dispatch<React.SetStateAction<LocationData[]>>; rtspConfigs: Record<string, RtspConfig>; onRtspChange: (id: string, c: RtspConfig) => void }) {
  const [modal, setModal] = useState<{ open: boolean; editId: string | null }>({ open: false, editId: null });
  const editingLoc = modal.editId ? locations.find((l) => l.id === modal.editId) ?? null : null;

  function handleSave(draft: Partial<LocationData> & { name: string }) {
    if (modal.editId) {
      setLocations((prev) => prev.map((l) => (l.id !== modal.editId ? l : { ...l, ...draft })));
    } else {
      const COLORS = ["#8b5cf6", "#ec4899", "#06b6d4"];
      const newLoc: LocationData = {
        id: `loc${Date.now()}`, name: draft.name, address: draft.address ?? "", city: draft.city ?? "", state: draft.state ?? "", zip: draft.zip ?? "",
        phone: draft.phone ?? "", email: draft.email ?? "", status: "open", isActive: true, manager: draft.manager ?? "", capacity: (draft.capacity as unknown as number) || 6,
        hoursOpen: draft.hoursOpen ?? "9:00 AM", hoursClose: draft.hoursClose ?? "7:00 PM", staffOnDuty: [],
        stats: { revenueToday: 0, revenueYesterday: 0, revenueWeek: 0, revenueMonth: 0, appointmentsToday: 0, appointmentsCompleted: 0, walkInsToday: 0, queueLength: 0, occupancyPercent: 0, clientsServedToday: 0, avgWaitMinutes: 0, topService: "—", rating: 0, newClientsMonth: 0 },
        alerts: [], hex: COLORS[locations.length % COLORS.length],
      };
      setLocations((prev) => [...prev, newLoc]);
    }
  }

  const toggleActive = (id: string) => setLocations((prev) => prev.map((l) => (l.id === id ? { ...l, isActive: !l.isActive, status: l.isActive ? "closed" : "open" } : l)));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <div><Title level={5} style={{ margin: 0 }}>Manage Locations</Title><Text type="secondary" style={{ fontSize: 12 }}>{locations.length} locations · {locations.filter((l) => l.isActive).length} active</Text></div>
        <Button type="primary" icon={<Plus size={14} />} onClick={() => setModal({ open: true, editId: null })}>Add Location</Button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
        {locations.map((loc) => (
          <Card key={loc.id} size="small" style={{ opacity: loc.isActive ? 1 : 0.6, borderColor: loc.isActive ? `${loc.hex}50` : undefined }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: loc.hex, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>{loc.name.charAt(0)}</div>
              <div style={{ flex: 1 }}><Text strong>{loc.name}</Text><div style={{ fontSize: 11, color: "#9ca3af" }}>{loc.city}, {loc.state}</div></div>
              <Switch checked={loc.isActive} onChange={() => toggleActive(loc.id)} />
            </div>
            <CameraFeedWidget locId={loc.id} locName={loc.name} config={rtspConfigs[loc.id] ?? { url: "", username: "", password: "", resolution: "auto", enabled: false }} onConfigChange={(c) => onRtspChange(loc.id, c)} />
            <Space direction="vertical" size={4} style={{ width: "100%", marginTop: 10, fontSize: 12 }}>
              <span><MapPin size={12} style={{ marginRight: 6 }} />{loc.address}, {loc.city} {loc.zip}</span>
              <span><Phone size={12} style={{ marginRight: 6 }} />{loc.phone}</span>
              <span><Mail size={12} style={{ marginRight: 6 }} />{loc.email}</span>
              <span><Clock size={12} style={{ marginRight: 6 }} />{loc.hoursOpen} – {loc.hoursClose}</span>
              <span><Users size={12} style={{ marginRight: 6 }} />{loc.capacity} stations · Manager: {loc.manager || "—"}</span>
            </Space>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #f0f0f0", marginTop: 10, paddingTop: 10 }}>
              <Space size={16} style={{ fontSize: 11 }}>
                <div><div style={{ fontWeight: 700 }}>{fmt$(loc.stats.revenueWeek)}</div><Text type="secondary" style={{ fontSize: 10 }}>This week</Text></div>
                <div><div style={{ fontWeight: 700 }}>{loc.stats.rating > 0 ? `${loc.stats.rating} ⭐` : "—"}</div><Text type="secondary" style={{ fontSize: 10 }}>Rating</Text></div>
              </Space>
              <Button size="small" icon={<Edit3 size={12} />} onClick={() => setModal({ open: true, editId: loc.id })}>Edit</Button>
            </div>
          </Card>
        ))}
      </div>

      <LocationEditModal open={modal.open} initial={editingLoc} onSave={handleSave} onClose={() => setModal({ open: false, editId: null })} />
    </div>
  );
}

function MiniBarChart({ data, locations }: { data: { label: string; [k: string]: number | string }[]; locations: LocationData[] }) {
  const max = Math.max(...data.flatMap((d) => locations.map((l) => Number(d[l.name]) || 0)), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 160, padding: "0 4px" }}>
      {data.map((d) => (
        <div key={d.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 130 }}>
            {locations.map((l) => (
              <div key={l.id} title={`${l.name}: ${d[l.name]}`} style={{ width: 10, height: `${(Number(d[l.name]) / max) * 100}%`, background: l.hex, borderRadius: "3px 3px 0 0", minHeight: 2 }} />
            ))}
          </div>
          <Text type="secondary" style={{ fontSize: 10 }}>{d.label}</Text>
        </div>
      ))}
    </div>
  );
}

const CHART_DATA: Record<string, { label: string; [k: string]: number | string }[]> = {
  week: [
    { label: "Mon", Downtown: 1200, Midtown: 820, Westside: 480 }, { label: "Tue", Downtown: 1450, Midtown: 950, Westside: 620 },
    { label: "Wed", Downtown: 1100, Midtown: 780, Westside: 510 }, { label: "Thu", Downtown: 1600, Midtown: 1050, Westside: 590 },
    { label: "Fri", Downtown: 1900, Midtown: 1300, Westside: 740 }, { label: "Sat", Downtown: 2200, Midtown: 1500, Westside: 880 },
  ],
  month: [
    { label: "Wk 1", Downtown: 8200, Midtown: 5400, Westside: 3100 }, { label: "Wk 2", Downtown: 9100, Midtown: 6200, Westside: 3800 },
    { label: "Wk 3", Downtown: 7800, Midtown: 5900, Westside: 4200 }, { label: "Wk 4", Downtown: 10200, Midtown: 6800, Westside: 4900 },
  ],
  year: [
    { label: "Mar", Downtown: 36500, Midtown: 24800, Westside: 13200 }, { label: "Apr", Downtown: 39200, Midtown: 26100, Westside: 14000 },
    { label: "May", Downtown: 37800, Midtown: 25500, Westside: 13900 }, { label: "Jun", Downtown: 38200, Midtown: 26400, Westside: 14800 },
    { label: "Jul", Downtown: 41500, Midtown: 28900, Westside: 16200 }, { label: "Aug", Downtown: 43800, Midtown: 31200, Westside: 17800 },
  ],
};

function AnalyticsTab({ locations }: { locations: LocationData[] }) {
  const [period, setPeriod] = useState<"week" | "month" | "year">("month");
  const data = CHART_DATA[period];
  const topStaff = [...STAFF_PERFORMANCE].sort((a, b) => b.clientsThisMonth - a.clientsThisMonth)[0];
  const topManager = STAFF_PERFORMANCE.filter((s) => s.isManager).sort((a, b) => b.revenueMonth - a.revenueMonth)[0];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div><Title level={5} style={{ margin: 0 }}>Cross-Location Analytics</Title><Text type="secondary" style={{ fontSize: 12 }}>Compare performance across all locations</Text></div>
        <Select value={period} onChange={setPeriod} options={[{ value: "week", label: "This Week" }, { value: "month", label: "This Month" }, { value: "year", label: "Last 6 Months" }]} style={{ width: 160 }} />
      </div>

      <Card size="small" title="Revenue Comparison" style={{ marginBottom: 16 }}>
        <MiniBarChart data={data} locations={locations} />
        <Space style={{ marginTop: 10 }}>{locations.map((l) => <Tag key={l.id} color={l.hex} style={{ background: l.hex, color: "#fff", border: "none" }}>{l.name}</Tag>)}</Space>
      </Card>

      <Card size="small" title="Location Performance Scorecard" style={{ marginBottom: 16 }}>
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={[...locations].sort((a, b) => b.stats.revenueMonth - a.stats.revenueMonth)}
          columns={[
            { title: "Location", key: "name", render: (_: unknown, loc: LocationData, i: number) => <Space><Text type="secondary" style={{ fontSize: 11 }}>#{i + 1}</Text><span style={{ width: 8, height: 8, borderRadius: "50%", background: loc.hex, display: "inline-block" }} /><Text strong style={{ fontSize: 13 }}>{loc.name}</Text></Space> },
            { title: "Revenue", key: "rev", render: (_: unknown, loc: LocationData) => <Text strong>{fmt$(loc.stats.revenueMonth)}</Text> },
            { title: "New Clients", key: "new", render: (_: unknown, loc: LocationData) => loc.stats.newClientsMonth },
            { title: "Avg Wait", key: "wait", render: (_: unknown, loc: LocationData) => <Tag color={loc.stats.avgWaitMinutes <= 10 ? "success" : loc.stats.avgWaitMinutes <= 18 ? "warning" : "error"}>{loc.stats.avgWaitMinutes} min</Tag> },
            { title: "Rating", key: "rating", render: (_: unknown, loc: LocationData) => loc.stats.rating > 0 ? <span>{loc.stats.rating} <Star size={11} fill="#f59e0b" color="#f59e0b" /></span> : "—" },
            { title: "Top Service", key: "svc", render: (_: unknown, loc: LocationData) => loc.stats.topService },
          ]}
        />
      </Card>

      <Title level={5}><Users size={16} style={{ marginRight: 6, verticalAlign: -2 }} />People Performance</Title>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
        {[{ sp: topStaff, tag: "Best Staff", tagColor: "gold" }, { sp: topManager, tag: "Best Manager", tagColor: "purple" }].map(({ sp, tag, tagColor }) => sp && (
          <Card key={tag} size="small">
            <Space align="start">
              <div style={{ width: 44, height: 44, borderRadius: 12, background: sp.hex, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 18 }}>{sp.name.charAt(0)}</div>
              <div>
                <Space size={6}><Text strong>{sp.name}</Text><Tag color={sp.hex} style={{ background: sp.hex, color: "#fff", border: "none", fontSize: 9 }}>{sp.location}</Tag><Tag color={tagColor} style={{ fontSize: 9 }}><Star size={8} style={{ marginRight: 2 }} />{tag}</Tag></Space>
                <div style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 6px" }}>{sp.topService} specialist · {sp.rating} rating</div>
                <Space size={16}>
                  <div><div style={{ fontWeight: 700 }}>{sp.clientsThisMonth}</div><Text type="secondary" style={{ fontSize: 10 }}>clients</Text></div>
                  <div><div style={{ fontWeight: 700 }}>{fmt$(sp.revenueMonth)}</div><Text type="secondary" style={{ fontSize: 10 }}>revenue</Text></div>
                  <div><div style={{ fontWeight: 700 }}>{Math.round(sp.retentionRate * 100)}%</div><Text type="secondary" style={{ fontSize: 10 }}>retention</Text></div>
                </Space>
              </div>
            </Space>
          </Card>
        ))}
      </div>

      <Card size="small" title={<span><TrendingUp size={14} style={{ marginRight: 6 }} />Staff Leaderboard & Raise Recommendations</span>}>
        {[...STAFF_PERFORMANCE].sort((a, b) => b.clientsThisMonth - a.clientsThisMonth).map((sp, i) => {
          const sig = raiseSignal(sp);
          return (
            <div key={sp.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: i > 0 ? "1px solid #f0f0f0" : undefined }}>
              <Text type="secondary" style={{ width: 20, fontSize: 11 }}>#{i + 1}</Text>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: sp.hex, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>{sp.name.charAt(0)}</div>
              <div style={{ flex: 1 }}>
                <Space size={6}><Text strong style={{ fontSize: 13 }}>{sp.name}</Text>{sp.isManager && <Tag style={{ fontSize: 9 }}>Mgr</Tag>}<Tag color={sp.hex} style={{ background: sp.hex, color: "#fff", border: "none", fontSize: 9 }}>{sp.location}</Tag></Space>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{sp.topService} · {Math.round(sp.retentionRate * 100)}% retention</div>
              </div>
              <Space size={20} style={{ fontSize: 12 }}>
                <div style={{ textAlign: "center" }}><div style={{ fontWeight: 700 }}>{sp.clientsThisMonth}</div><Text type="secondary" style={{ fontSize: 10 }}>clients</Text></div>
                <div style={{ textAlign: "center", color: sig.growth >= 0 ? "#059669" : "#ef4444" }}><div style={{ fontWeight: 700 }}>{sig.growth >= 0 ? "▲" : "▼"} {Math.abs(sig.growth).toFixed(0)}%</div><Text type="secondary" style={{ fontSize: 10 }}>growth</Text></div>
              </Space>
              <Tag color={sig.color}>{sig.label}</Tag>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

export default function AdminMultiLocationPage() {
  const [locations, setLocations] = useState<LocationData[]>(INITIAL_LOCATIONS);
  const [rtspConfigs, setRtspConfigs] = useState<Record<string, RtspConfig>>(DEFAULT_RTSP);

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center" }}><Building2 size={16} color="#fff" /></div>
            <Title level={3} style={{ margin: 0 }}>Multi-Location HQ</Title>
          </div>
          <Text type="secondary" style={{ fontSize: 13 }}>{locations.length} locations · {locations.filter((l) => l.isActive).length} active</Text>
        </div>
        <Space wrap>
          {locations.map((loc) => <Tag key={loc.id} style={{ background: `${loc.hex}15`, color: loc.hex, borderColor: `${loc.hex}50` }}>● {loc.name}</Tag>)}
        </Space>
      </div>

      <Alert
        style={{ marginBottom: 16 }}
        type="warning"
        showIcon
        message="Demo preview — single-location schema underneath"
        description="Fully interactive so you can try the flow (including simulated live camera feeds), but the real backend currently assumes one location per business. Supporting this for real is an architecture change, not just a new screen — worth prioritizing only once there are actual multi-location customers."
      />

      <Tabs
        items={[
          { key: "dashboard", label: <span><Activity size={14} style={{ marginRight: 5 }} />Live Dashboard</span>, children: <DashboardTab locations={locations} setLocations={setLocations} rtspConfigs={rtspConfigs} onRtspChange={(id, c) => setRtspConfigs((p) => ({ ...p, [id]: c }))} /> },
          { key: "locations", label: <span><Building2 size={14} style={{ marginRight: 5 }} />Locations</span>, children: <LocationsTab locations={locations} setLocations={setLocations} rtspConfigs={rtspConfigs} onRtspChange={(id, c) => setRtspConfigs((p) => ({ ...p, [id]: c }))} /> },
          { key: "analytics", label: <span><Building2 size={14} style={{ marginRight: 5 }} />Analytics</span>, children: <AnalyticsTab locations={locations} /> },
        ]}
      />
    </div>
  );
}
